"use client";

import { useTranslations } from "next-intl";
import React, { useEffect } from "react";
import Container from "apps/website/components/layout/Container/Container";
import {
  IterativeInputFields,
} from "apps/website/components/form/Input/v2/IterativeInputField/IterativeInputField";
import {
  useSignupFlowStore,
} from "libs/state/src/lib/stores/signupFlow/hooks/useSignUpFlowStore";
import {
  MessagingCard,
} from "apps/website/components/feature/MessagingCard/MessagingCard";
import { ContentWrapper } from "apps/website/components/sign-up/fresh/common";
import { SignupFlowSlug, useFreshSignupFlowNavigation } from "@./state";
import QuoteBanner from "apps/website/components/form/QuoteBanner/QuoteBanner";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import Illustration from "apps/website/components/base/Illustration/Illustration";
import { StickyCTA } from "apps/website/components/feature/StickyCTA/StickyCTA";
import { scrollToContent } from "apps/website/utils/website/scrollToContent";
import { Polygon } from "apps/website/components/feature/Polygon/Polygon";
import {
  createEmailContactLink,
  renderRichTranslations,
} from "apps/website/utils/translations/richText";
import { useEvent } from "libs/hooks/src/lib/event/useEvent";

import { useCatNamesForm } from "./hooks/useCatNamesForm";
import {
  extractCatNamesFromStore,
  hasMoreThanFourCats,
  hasMultiCatDiscount,
} from "./utils";

const backgroundColor = "light-grey-75";

/*
This component is currently unused. CatNamesNew is the new component.
*/
export default function CatNames() {
  const { createCats, cats } = useSignupFlowStore((state) => ({
    createCats: state.createCats,
    cats: state.cats,
  }));

  const storedCatNames = extractCatNamesFromStore(cats);
  const { dispatchEvent } = useEvent();

  const {
    catNames,
    errors,
    errorIndexes,
    updateName,
    addName,
    deleteName,
    validateAll,
    getFormattedCatEntries,
  } = useCatNamesForm(storedCatNames);

  const hasErrors = Object.keys(errors).length > 0;

  const t = useTranslations("SUFv2.CatNames");

  const { goToNext, canContinue } = useFreshSignupFlowNavigation(SignupFlowSlug.CAT_NAMES);

  /*
  As page is currently unused, this event is not fired.
  */
  useEffect(() => {
    dispatchEvent("cat_count", {
      category: "manage_fresh",
      label: "cat_count_interaction",
      cat_count: catNames.length,
      variant: "control",
    });
  }, [ catNames.length, dispatchEvent ]);

  const handleSubmit = () => {
    const isValid = validateAll();

    if (isValid && canContinue) {
      const entries = getFormattedCatEntries();

      dispatchEvent("cat_count", {
        category: "manage_fresh",
        label: "cat_count_submitted",
        cat_count: entries.length,
        variant: "control",
      });

      createCats(entries);
      goToNext();
    }
  };

  const handleEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      if (hasErrors) {
        scrollToContent("catNamesErrorMessage");
      } else if (hasMoreThanFourCats(catNames)) {
        scrollToContent("catNamesMoreThanFourCats");
      } else if (hasMultiCatDiscount(catNames)) {
        scrollToContent("catNamesMultiCatDiscount");
      }
    });
  }, [ hasErrors, catNames ]);

  return (
    <>
      <Container size="md" padding="none" className="relative z-10 pb-24">

        <div className="max-w-[393px] mx-auto">
          <QuoteBanner
            quote={t("quoteBanner.quote")}
            author={t("quoteBanner.author")}
            image={{ src: "/images/people/Dominique.webp", width: 48, height: 58 }}
            styleOptions={{ bannerFillColour: "light-blue", frameFillColour: "green" }}
          />
        </div>

        <Spacer size="gap-default" />

        <ContentWrapper backgroundColour="light-grey" styleOptions={{ paddingX: 6, paddingY: 5, borderRadius: "md", direction: "column", justify: "center", align: "center", gapY: 4 }}>

          <IterativeInputFields
            items={catNames }
            addAnotherInputLabel={t("catNameInput.addAnotherLabel")}
            placeholder={t("catNameInput.placeholder")}
            srLabel={t("catNameInput.srLabel")}
            legend={t("catNameInput.legend")}
            maxItems={4}
            minItemsForDelete={1}
            size="full"
            styleOptions={{ textAlign: "center" }}
            errorIndexes={errorIndexes}
            onItemChange={updateName}
            onItemBlur={updateName}
            onAddAnother={addName}
            onDeleteItem={deleteName}
            onKeyDown={handleEnterKeyDown}
          />

          { hasErrors && (
            <MessagingCard
              component="functional"
              description={t("errorMessages.inputs")}
              icon={{ icon: "warningBold" }}
              styleOptions={{ backgroundColor }}
              id="catNamesErrorMessage"
            />
          ) }

          { (hasMultiCatDiscount(catNames) && !hasErrors) && (
            <MessagingCard
              component="functional"
              description={t("multiCatDiscount", { count: catNames.length })}
              icon={{ icon: "currency", color: "success" }}
              styleOptions={{ backgroundColor }}
              id="catNamesMultiCatDiscount"
            />
          ) }

          { (hasMoreThanFourCats(catNames) && !hasErrors) && (
            <MessagingCard
              component="functional"
              description={renderRichTranslations(t, "moreThanFourCats.cardBody", { email: (chunks: React.ReactNode) => createEmailContactLink(chunks, "meow@katkin.com", 2, "normal") })}
              icon={{ icon: "informationHeavy", className: "text-charcoal shrink-0" }}
              styleOptions={{ backgroundColor }}
              id="catNamesMoreThanFourCats"
            />
          ) }

        </ContentWrapper>

      </Container>

      <Polygon
        className="fixed bottom-0 left-0 w-full md:h-[245px] h-[195px]"
        bgColor="sand-200"
        clipPath={{
          topLeft: "0% 15%",
          topRight: "100% 0%",
          bottomRight: "100% 100%",
          bottomLeft: "0% 100%",
        }}
      />

      <div className="w-full max-w-[315px] md:max-w-[380px] mx-auto fixed md:bottom-24 bottom-20 left-0 right-0 z-[2] overflow-clip" data-testid="fresh-start-illustration">
        <Illustration illustration="freshStart" size="fit" />
      </div>

      <div className="w-full relative">
        <StickyCTA position="fixed"
          button={{
            text: t("ctaButton"),
            onClick: handleSubmit,
            icon: { name: "arrowRightShort", position: "trailing" },
            color: "primary",
            design: "default",
            ariaLabel: t("ctaAriaLabel"),
            size: "full-width",
          }} />
      </div>
    </>
  );
}
